import { call, put, SagaGenerator, select, take, takeEvery, takeLatest } from "typed-redux-saga";
import { Action, AnyAction } from "typescript-fsa";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { JsonRpc2Response, Query } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { OverviewEngineSelectors } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { WorkflowsActions } from "@com.mgmtp.a12.workflows/workflows-core/lib/actions";

import { createCustomNotificationAction } from "../../app/actions";
import { updateIntegratedDocument } from "../../app/integratedDocuments/actions";
import { getFVManifest } from "../../app/slices/fachverfahren/selectors";
import {
	didUpdateOverviewDocument,
	DidUpdateOverviewDocumentPayload
} from "../../component/auxiliaryOverviewEngine/actions";
import { RESOURCE_KEYS } from "../../localization";
import { convertAppIdToKey } from "../../utilities/applicationKeyUtils";
import { assertNonNullable } from "../../utilities/assert";
import { hasUpdateDocumentPermission } from "../../utilities/permissionUtils";
import { query } from "../../utilities/rpcHelper";
import { selectHasNeededPermissions } from "../../utilities/user";

import { FRISTEN_ACTIVITY } from "../fristen/constants";
import { ANTRAG_DETAIL_ACTIVITY_ID, TASK_DETAIL_ACTIVITY_ID } from "../processOverview/store/actions";
import { isDocumentWithIdAndProcessMetadata, isDocumentWithProcessMetadata } from "../workflows/data/processMetadata";

import {
	reloadWiedervorlagen,
	requestUpdateDueWiedervorlagenCount,
	RequestUpdateDueWiedervorlagenCountPayload
} from "./actions";
import { PATHS, WIEDERVORLAGEN_OVERVIEW_ACTIVITY, WIEDERVORLAGEN_RUNTIME_MODULE } from "./constants";
import { dataModels, formModels, prefix } from "./moduleResources";

const ERROR_KEYS = RESOURCE_KEYS.wf.notification.error;

/**
 * Zeigt die Overview für Wiedervorlagen an oder versteckt sie wieder
 */
export function* openWiedervorlagenSaga(): SagaGenerator<void> {
	yield* takeLatest((action: AnyAction) => {
		return (
			FormEngineActions.event.match(action) &&
			Events.eventButtonTriggered.match(action.payload.engineEvent) &&
			action.payload.engineEvent.payload.name === "showWiedervorlagen" &&
			!!action.payload.engineEvent.payload.buttonPath
		);
	}, openWiedervorlagen);
}

function* openWiedervorlagen(action: Action<FormEngineActions.FormEngineEventActions>): SagaGenerator<void> {
	const wasOpenBefore = yield* select(ActivitySelectors.activityById(WIEDERVORLAGEN_OVERVIEW_ACTIVITY));
	if (wasOpenBefore) {
		yield* put(ActivityActions.cancel({ activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY }));
		return;
	}

	const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));

	if (!activity) {
		throw new Error("Keine Activity gefunden.");
	}

	const sourceDescriptor = { ...(activity?.descriptor ?? {}) } as Record<string, string | undefined>;

	const processId = yield* call(getProcessIDFromActivity, activity.id);

	if (!processId) {
		console.error("No process ID in task activity");
		return;
	}
	const fvId = sourceDescriptor.fachverfahren;

	const activityDescriptor = {
		fachverfahren: fvId,
		instance: undefined,
		model: dataModels.WIEDERVORLAGE_D,
		processId
	};
	const fvKey = fvId ? convertAppIdToKey(fvId) : undefined;
	const data = yield* select(ActivitySelectors.data(TASK_DETAIL_ACTIVITY_ID));
	const isFinished =
		FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithProcessMetadata(data.document)
			? data.document.root.processMetadata.isFinished
			: false;
	const taskUiState = yield* select(FormEngineSelectors.uiState(TASK_DETAIL_ACTIVITY_ID));
	const isTaskReadOnly = (taskUiState?.readonly ?? false) && !isFinished;

	const hasPermission = yield* select(selectHasNeededPermissions(hasUpdateDocumentPermission, fvKey));
	const fristenActivity = yield* select(ActivitySelectors.activityById(FRISTEN_ACTIVITY));
	if (fristenActivity) {
		yield* put(ActivityActions.cancel({ activityId: FRISTEN_ACTIVITY }));
	}
	yield* put(
		ActivityActions.create({
			activityDescriptor,
			initiatingActivityId: action.payload.activityId,
			activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY,
			slices: {
				uiState: {
					readonly: !hasPermission || isTaskReadOnly
				}
			}
		})
	);
}

/**
 * Setzt die Zahl der fälligen oder bald fälligen Wiedervorlagen initial (Badge am Knopf "Wiedervorlagen")
 */
export function* setDueWiedervorlagenCountOnLoadSaga(): SagaGenerator<void> {
	while (true) {
		const action = yield* take(reloadWiedervorlagen);
		const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));
		if (!activity) {
			throw new Error("Could not find activity with ID " + action.payload.activityId);
		}

		const fvManifest = activity.descriptor.fachverfahren
			? yield* select(getFVManifest(activity.descriptor.fachverfahren))
			: undefined;
		const hasWiedervorlageModule = fvManifest?.runtimeModules?.find(
			it => it.name === WIEDERVORLAGEN_RUNTIME_MODULE
		);

		if (activity?.descriptor?.formModel === prefix(formModels.ANTRAG_F, activity) && hasWiedervorlageModule) {
			yield* put(
				requestUpdateDueWiedervorlagenCount({
					activityId: activity.id
				})
			);
		}
	}
}

/**
 * Setzt die Zahl der fälligen oder bald fälligen Wiedervorlagen neu, nachdem sich Änderungen an den WVL-Dokumenten
 * ergeben haben.
 */
export function* resetDueWiedervorlagenCountOnListChange(): SagaGenerator<void> {
	yield* takeEvery(ActivityActions.setData, function* ({ payload: { activityId } }) {
		if (activityId === WIEDERVORLAGEN_OVERVIEW_ACTIVITY) {
			const activity: Activity | undefined = yield* select(ActivitySelectors.activityById(activityId));
			if (!activity?.dataHolders?.find(it => "documents" in (it.data ?? {}))) {
				return;
			}

			const parentActivity: Activity | undefined = activity.initiatingActivityId
				? yield* select(ActivitySelectors.activityById(activity.initiatingActivityId))
				: undefined;

			if (
				!parentActivity ||
				parentActivity.descriptor.formModel !== prefix(formModels.ANTRAG_F, parentActivity)
			) {
				return;
			}

			if (!activity.descriptor.processId) {
				throw new Error("Could not find processId");
			}
			yield* put(
				requestUpdateDueWiedervorlagenCount({
					activityId: parentActivity.id
				})
			);
		}
	});
}

export function* setDueWiedervorlagenCount(): SagaGenerator<void> {
	yield* takeEvery(
		requestUpdateDueWiedervorlagenCount,
		function* ({ payload: { activityId } }: Action<RequestUpdateDueWiedervorlagenCountPayload>) {
			const processId = yield* call(getProcessIDFromActivity, activityId);

			if (!processId) {
				// Not a task activity
				return;
			}

			const activity = yield* select(ActivitySelectors.activityById(activityId));

			if (!activity) {
				throw new Error(`Activity ${activityId} not found`);
			}

			const count = yield* call(loadWiedervorlagenCount, processId);
			const oldData = yield* select(ActivitySelectors.data(activityId));
			const newData = {
				...oldData,
				wiedervorlagenCount: count
			};
			yield* put(
				ActivityActions.setData({
					activityId,
					data: newData
				})
			);
		}
	);
}

export function* handleDidUpdateWiedervorlageInOverviewSaga(): SagaGenerator<void> {
	yield* takeLatest(
		(action: AnyAction) => {
			return (
				didUpdateOverviewDocument.match(action) &&
				action.payload.updatedDocument.modelId === dataModels.WIEDERVORLAGE_D
			);
		},
		function* ({ payload: { updatedDocument, activityId } }: Action<DidUpdateOverviewDocumentPayload>) {
			const processId: string = (updatedDocument as Activity.Data.Document)?.root?.ProcessId;
			assertNonNullable(processId);

			yield* put(
				updateIntegratedDocument({
					subdocument: updatedDocument,
					subdocumentId: (updatedDocument.id! as string).split("/")[1],
					processId
				})
			);

			yield* put(requestUpdateDueWiedervorlagenCount({ activityId: ANTRAG_DETAIL_ACTIVITY_ID }));

			const overviewState = yield* select(OverviewEngineSelectors.uiStateWithoutDefaults(activityId));
			if (overviewState?.sorting && overviewState.sorting.some(s => s.path === PATHS.Erledigt)) {
				yield* put(ActivityActions.reloadData({ activityId: activityId }));
			}
		}
	);
}

/**
 * Setzt einen QUERY-Request mit den in filterQuery.ts festgelegten Feldfiltern ab.
 */
export async function loadWiedervorlagenCount(processId: string): Promise<number> {
	const queryRoot: Query.QueryRoot = {
		projectionName: "document",
		targetDocumentModel: dataModels.WIEDERVORLAGE_D,
		constraint: {
			operator: "and",
			operands: [
				{
					operator: "not",
					operand: {
						operator: "exact_match",
						field: PATHS.Erledigt,
						value: "true"
					}
				},
				{
					operator: "date_range",
					field: PATHS.Faelligkeitsdatum,
					to: daysFromNow(5)
				},
				{
					operator: "exact_match",
					field: PATHS.ProcessId,
					value: processId
				}
			]
		},
		paging: {
			pageNumber: 0,
			pageSize: 1000
		},
		fields: ["/__meta/docRef"]
	};

	return await query(queryRoot).then(e => e?.length ?? 0);
}

function daysFromNow(days: number): string {
	const newDate: Date = new Date();
	newDate.setDate(newDate.getDate() + days);
	return newDate.toISOString().split("T")[0];
}

function* getProcessIDFromActivity(activityId: string): SagaGenerator<string | undefined> {
	const data = yield* select(ActivitySelectors.data(activityId));
	if (FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithIdAndProcessMetadata(data.document)) {
		return data.document.root.processMetadata.processId;
	}
	return undefined;
}

export function* closeWiedervorlagenSaga(): SagaGenerator<void> {
	yield* takeLatest(WorkflowsActions.completeTask.done, function* () {
		const wiedervorlageActivity = yield* select(ActivitySelectors.activityById(WIEDERVORLAGEN_OVERVIEW_ACTIVITY));
		if (wiedervorlageActivity) {
			yield* put(ActivityActions.cancel({ activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY }));
		}
	});
}

export function* handleCommitErrorSaga(): SagaGenerator<void> {
	yield* takeLatest(ActivityActions.commit.failed, function* (action) {
		const addDocumentError = Object.values(action.payload.error)
			.flatMap((it: any) => ("error" in it ? [it.error] : []))
			.filter((it: unknown): it is JsonRpc2Response.JsonRpc2Error =>
				JsonRpc2Response.JsonRpc2Error.isInstance(it)
			)
			.map(it => it.data)
			.filter((it: unknown): it is JsonRpc2Response.Exception => JsonRpc2Response.Exception.isInstance(it))
			.find(it => "error.add_document.error.title" === it.title.key);
		if (!addDocumentError) {
			return;
		}
		yield* put(
			createCustomNotificationAction({
				activityId: action.payload.params.activityId,
				severity: "error",
				title: { key: ERROR_KEYS.createDocumentTitle },
				message: { key: ERROR_KEYS.createDocumentMessage },
				group: `commit:${action.payload.params.activityId}`
			})
		);
	});
}

export const wiedervorlageSagas = [
	openWiedervorlagenSaga,
	setDueWiedervorlagenCountOnLoadSaga,
	resetDueWiedervorlagenCountOnListChange,
	setDueWiedervorlagenCount,
	handleDidUpdateWiedervorlageInOverviewSaga,
	closeWiedervorlagenSaga,
	handleCommitErrorSaga
];
